import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import GuestClaimModal from "@/components/GuestClaimModal";
import { Wallet, Calculator, Briefcase, Settings as SettingsIcon, ShieldCheck, ArrowLeft, FileSignature, Repeat, Package, Loader2, Boxes, Plane, Banknote, ReceiptText, FileBox } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Info-Sperrseite für Gäste: Finanzen/Steuern/Steuerberater/Einstellungen sind im
// Demo-Modus NICHT funktional — hier erklären wir nur, was im Live-Modus passiert.
export const GUEST_LOCKED_AREAS = {
  finances: {
    icon: Wallet,
    title: "Finanzen & Belege",
    text: "Im Live-Modus erfasst du hier deine Ausgaben und Belege – auch per Foto: Die KI liest Betrag, Datum und Steuersatz automatisch aus und ordnet den Beleg zu. So bleibt deine Buchhaltung ohne Aufwand aktuell.",
  },
  taxes: {
    icon: Calculator,
    title: "Steuern & Rücklagen",
    text: "Im Live-Modus berechnet ivoliq aus deinen echten Zahlen die Steuer-Rücklage, die USt-Voranmeldung und alle wichtigen Fristen – damit du nie eine Zahlung oder einen Termin verpasst.",
  },
  advisor: {
    icon: Briefcase,
    title: "Steuerberater-Bereich",
    text: "Im Live-Modus teilst du hier ausgewählte Daten sicher mit deiner Kanzlei, tauschst Belege und Auswertungen aus und arbeitest ohne E-Mail-Chaos direkt zusammen.",
  },
  settings: {
    icon: SettingsIcon,
    title: "Einstellungen",
    text: "Im Live-Modus hinterlegst du hier deine Firmendaten, dein Logo, deine Steuernummer und dein Rechnungsdesign – die Basis für rechtskonforme, professionelle Rechnungen.",
    preview: { kind: "invoice", label: "So sieht deine fertige Rechnung aus:" },
  },
  quotes: {
    icon: FileSignature,
    title: "Angebote",
    text: "Im Live-Modus erstellst du hier verbindliche Angebote mit PDF, die deine Kund:innen online annehmen können – und wandelst sie mit einem Klick in eine Rechnung um.",
    preview: { kind: "quote", label: "So sieht ein fertiges Angebot aus:" },
  },
  recurring: {
    icon: Repeat,
    title: "Retainer & Abos",
    text: "Im Live-Modus legst du hier wiederkehrende Rechnungen an – ivoliq erstellt und versendet sie automatisch im gewählten Rhythmus, ganz ohne manuelle Arbeit.",
    preview: { kind: "invoice", label: "So sieht eine automatisch erzeugte Rechnung aus:" },
  },
  articles: {
    icon: Package,
    title: "Standardartikel",
    text: "Im Live-Modus hinterlegst du hier häufig genutzte Leistungen und Preise, um Rechnungen und Angebote in Sekunden zu füllen.",
  },
  anlagen: {
    icon: Boxes,
    title: "Anlagen & Abschreibung",
    text: "Im Live-Modus verwaltest du hier dein Anlagevermögen – ivoliq berechnet automatisch die Abschreibung (AfA) über die Nutzungsdauer und ordnet sie deiner Steuer zu.",
  },
  reisen: {
    icon: Plane,
    title: "Reisekosten",
    text: "Im Live-Modus erfasst du hier Geschäftsreisen mit Verpflegungspauschalen, Kilometergeld und Übernachtungen – steuerlich korrekt und mit einem Klick als Beleg.",
  },
  banking: {
    icon: Banknote,
    title: "Banking",
    text: "Im Live-Modus verbindest du hier dein Geschäftskonto, gleichst Umsätze automatisch mit Rechnungen ab und erkennst offene Zahlungen sofort.",
  },
  voranmeldung: {
    icon: ReceiptText,
    title: "USt-Voranmeldung",
    text: "Im Live-Modus erstellt ivoliq deine Umsatzsteuer-Voranmeldung aus deinen echten Zahlen – fristgerecht, ELSTER-konform und ohne Rechnerei.",
  },
  exports: {
    icon: FileBox,
    title: "Exporte",
    text: "Im Live-Modus exportierst du hier deine Daten für Steuerberater und Finanzamt – DATEV, ELSTER und CSV, sauber aufbereitet.",
  },
};

const PREVIEW_ENDPOINT = {
  quote: `${BACKEND}/api/quotes/sample-preview`,
  invoice: `${BACKEND}/api/invoices/sample-preview`,
};
const PREVIEW_ALT = {
  quote: "Muster-Angebot als PDF-Vorschau",
  invoice: "Muster-Rechnung als PDF-Vorschau",
};

// Muster-Dokument-Vorschau (Angebot/Rechnung) im Sperrscreen: zeigt Gästen sofort den Mehrwert.
function SampleDocPreview({ kind, label }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let active = true;
    axios
      .get(PREVIEW_ENDPOINT[kind], { responseType: "blob", withCredentials: true })
      .then((res) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind]);

  if (error) return null;

  return (
    <div className="mt-8 w-full" data-testid={`guest-${kind}-sample-preview`}>
      <p className="text-sm font-medium text-slate-500 mb-3">{label}</p>
      <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-lg">
        {url ? (
          <img
            src={url}
            alt={PREVIEW_ALT[kind]}
            className="w-full rounded-md bg-white shadow-sm"
            data-testid={`guest-${kind}-sample-image`}
          />
        ) : (
          <div className="flex h-72 items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function GuestAreaLock({ area }) {
  const navigate = useNavigate();
  const [claimOpen, setClaimOpen] = useState(false);
  const cfg = GUEST_LOCKED_AREAS[area];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const hasPreview = !!cfg.preview;

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid={`guest-area-lock-${area}`}>
      <div className={`${hasPreview ? "max-w-2xl" : "max-w-lg"} w-full text-center rounded-2xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm`}>
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
          <Icon className="h-7 w-7 text-emerald-600" />
        </div>
        <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 mb-3">
          Nur im Live-Modus
        </span>
        <h1 className="font-heading text-2xl font-bold text-slate-900">{cfg.title}</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">{cfg.text}</p>

        {hasPreview && <SampleDocPreview kind={cfg.preview.kind} label={cfg.preview.label} />}

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={() => setClaimOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
            data-testid={`guest-area-lock-claim-${area}`}
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Konto sichern & freischalten
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-slate-500 hover:text-slate-700 w-full sm:w-auto"
            data-testid={`guest-area-lock-back-${area}`}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Zurück zur Demo
          </Button>
        </div>
      </div>
      <GuestClaimModal open={claimOpen} onOpenChange={setClaimOpen} />
    </div>
  );
}
